import { FC, memo, useState, useEffect, ChangeEvent } from 'react';
import {
  useTheme,
  useMediaQuery,
  Stack,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { setToastMessage } from '@stores/App/slice';
import { useDispatch } from 'react-redux';
import { useSelfServeAddGameMutation, AddNewGamePayload } from '@api/selfServe';
import {
  PLAY_STORE_LINK_REGEX,
  APP_STORE_LINK_REGEX,
  APP_STORE_LINK_REGEX_2,
} from '@constants/constants';
import { TOAST_TYPES } from '@constants/constants';
import { useLazyGetCategoriesAndGenresQuery } from '@api/games';

interface AddGameProps {
  close?: (cancel?: boolean) => void;
}

const AddGame: FC<AddGameProps> = memo(({ close }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [addGameApi, { isLoading: isAddGameLoading }] = useSelfServeAddGameMutation();
  const dispatch = useDispatch();

  const [linkToPlayStore, setLinkToPlayStore] = useState<string>('');
  const [linkToAppStore, setLinkToAppStore] = useState<string>('');
  const [touchedPlayStoreLink, setTouchedPlayStoreLink] = useState<boolean>(false);
  const [touchedAppStoreLink, setTouchedAppStoreLink] = useState<boolean>(false);
  const [touchedCategory, setTouchedCategory] = useState<boolean>(false);
  const [touchedGenre, setTouchedGenre] = useState<boolean>(false);
  const [errorState, setErrorState] = useState({
    linkToPlayStore: false,
    linkToAppStore: false,
    category: false,
    genre: false,
  });
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [category, setCategory] = useState<string>('');
  const [genre, setGenre] = useState<string>('');

  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [genreOptions, setGenreOptions] = useState<any[]>([]);
  const [fetchCategoryAndGenreOptionsApi] = useLazyGetCategoriesAndGenresQuery();

  const handleSaveApplicationDetails = async (): Promise<void> => {
    try {
      const payload: AddNewGamePayload = {
        appStoreUrl: linkToAppStore || undefined,
        playStoreUrl: linkToPlayStore || undefined,
        category: category,
        genre: genre,
      };

      await addGameApi(payload).unwrap();

      !!close && close();
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || null,
        }),
      );
    }
  };

  const fetchCategoryAndGenreOptions = async (): Promise<void> => {
    try {
      const response = await fetchCategoryAndGenreOptionsApi().unwrap();
      const { data } = response;

      setCategoryOptions(data.categories);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || null,
        }),
      );
    }
  };

  useEffect(() => {
    fetchCategoryAndGenreOptions();
  }, []);

  useEffect(() => {
    setGenre('');
    if (category === null) return;

    let genres: string[] = [];
    for (const cat of categoryOptions) {
      if (cat.name !== category) continue;

      genres = cat.genres;
      break;
    }

    setGenreOptions(genres);
  }, [category]);

  useEffect(() => {
    let newErrorState = {
      ...errorState,
    };

    const isPlayStoreLinkValid = PLAY_STORE_LINK_REGEX.test(linkToPlayStore);
    const isAppStoreLinkValid =
      APP_STORE_LINK_REGEX.test(linkToAppStore) || APP_STORE_LINK_REGEX_2.test(linkToAppStore);

    if (!linkToPlayStore && !linkToAppStore) {
      // If both are empty, set errors and invalidate form
      newErrorState = {
        linkToPlayStore: true,
        linkToAppStore: true,
        category: !category,
        genre: !genre,
      };
      setIsFormValid(false);
    } else {
      newErrorState = {
        linkToPlayStore: linkToPlayStore.length > 0 && !isPlayStoreLinkValid,
        linkToAppStore: linkToAppStore.length > 0 && !isAppStoreLinkValid,
        category: !category,
        genre: !genre,
      };

      const isValidForm =
        ((isPlayStoreLinkValid && isAppStoreLinkValid) ||
          (isPlayStoreLinkValid && !linkToAppStore) ||
          (isAppStoreLinkValid && !linkToPlayStore)) &&
        !!category &&
        !!genre;

      setIsFormValid(isValidForm);
    }

    setErrorState(newErrorState);
  }, [linkToPlayStore, linkToAppStore, category, genre]);

  return (
    <Stack width={'390px'} maxWidth={isMobile ? 'min(390px, 80vw)' : '390px'} height={'100%'}>
      <Stack flex={1}>
        <Stack padding={'1.5rem 1rem'} direction={'row'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'} gap={'0.75rem'}>
            <Typography
              fontSize={'1.375rem'}
              lineHeight={'1.875rem'}
              fontWeight={700}
              color={'#6C6C6C'}
            >
              Add Game
            </Typography>
          </Stack>

          <Stack>
            <IconButton
              onClick={() => close && close(true)}
              disableFocusRipple
              disableRipple
              disableTouchRipple
            >
              <CloseIcon
                sx={{
                  color: '#162C36',
                }}
              />
            </IconButton>
          </Stack>
        </Stack>
        <Stack flex={1} gap={'0.75rem'} padding={'0.5rem 1.5rem'}>
          <TextField
            label="Link to Play Store"
            disabled={isAddGameLoading}
            value={linkToPlayStore}
            error={touchedPlayStoreLink && errorState.linkToPlayStore}
            onBlur={() => setTouchedPlayStoreLink(true)}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setLinkToPlayStore(e.target.value)
            }
          />

          <TextField
            label="Link to App Store"
            disabled={isAddGameLoading}
            value={linkToAppStore}
            error={touchedAppStoreLink && errorState.linkToAppStore}
            onBlur={() => setTouchedAppStoreLink(true)}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setLinkToAppStore(e.target.value)
            }
          />

          <FormControl fullWidth>
            <InputLabel id="category-label">Category</InputLabel>

            <Select
              onBlur={() => setTouchedCategory(true)}
              error={touchedCategory && errorState.category}
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value)}
            >
              {categoryOptions.map((category, index) => (
                <MenuItem key={index} value={category.name}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="genre-label">Genre</InputLabel>

            <Select
              onBlur={() => setTouchedGenre(true)}
              disabled={genreOptions.length === 0}
              error={touchedGenre && errorState.genre}
              value={genre}
              label="Genre"
              onChange={(e) => setGenre(e.target.value)}
            >
              {genreOptions.map((g, index) => (
                <MenuItem key={index} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      <Stack padding={'1rem 1.5rem'} direction={'row'} borderTop={'1px solid #E8EAEB'}>
        <Button
          onClick={handleSaveApplicationDetails}
          disabled={isAddGameLoading || !isFormValid}
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          variant="contained"
          sx={{
            padding: '0.625rem 1.5rem',
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
            background: '#2F736E',
            color: '#fff',
          }}
        >
          {isAddGameLoading ? (
            <CircularProgress
              size={'1rem'}
              sx={{
                marginRight: '0.5rem',
                color: '#9AD6D1',
              }}
            />
          ) : (
            <></>
          )}
          {isAddGameLoading ? 'Saving' : 'Save'}
        </Button>
        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={() => close && close(true)}
          sx={{
            padding: '0.625rem 1.5rem',
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
            color: '#51B8B0',
            ':hover': {
              background: 'none',
            },
          }}
        >
          Close
        </Button>
      </Stack>
    </Stack>
  );
});

export default AddGame;
